import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { euro, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Modal, Field, inputCls, Badge, EmptyState, Toolbar } from "@/components/kit";
import { Plus, Trash2, UserCog, Pencil, CalendarDays, Check, X } from "lucide-react";

const EMP_EMPTY = { name: "", email: "", position: "", department: "", phone: "", salary: 0, hire_date: "", status: "actif" };
const LEAVE_EMPTY = { employee_id: "", employee_name: "", type: "congé payé", start_date: "", end_date: "", days: 1, reason: "" };

export default function HR() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);

  const [empOpen, setEmpOpen] = useState(false);
  const [empForm, setEmpForm] = useState(EMP_EMPTY);
  const [empEditId, setEmpEditId] = useState(null);

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState(LEAVE_EMPTY);
  const [saving, setSaving] = useState(false);

  const loadEmp = () => api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  const loadLeaves = () => api.get("/leaves").then((r) => setLeaves(r.data)).catch(() => {});
  useEffect(() => { loadEmp(); loadLeaves(); }, []);

  // Employees
  const openNewEmp = () => { setEmpForm(EMP_EMPTY); setEmpEditId(null); setEmpOpen(true); };
  const openEditEmp = (e) => { setEmpForm({ ...EMP_EMPTY, ...e }); setEmpEditId(e.id); setEmpOpen(true); };
  const saveEmp = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...empForm, salary: Number(empForm.salary) };
    try {
      if (empEditId) await api.put(`/employees/${empEditId}`, payload);
      else await api.post("/employees", payload);
      toast.success(empEditId ? "Employé mis à jour" : "Employé ajouté");
      setEmpOpen(false); loadEmp();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delEmp = async (id) => {
    if (!window.confirm("Supprimer cet employé ?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Supprimé"); loadEmp(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  // Leaves
  const openNewLeave = () => { setLeaveForm(LEAVE_EMPTY); setLeaveOpen(true); };
  const saveLeave = async (e) => {
    e.preventDefault(); setSaving(true);
    const emp = employees.find((x) => x.id === leaveForm.employee_id);
    const payload = { ...leaveForm, employee_name: emp ? emp.name : leaveForm.employee_name, days: Number(leaveForm.days) };
    try {
      await api.post("/leaves", payload);
      toast.success("Demande soumise");
      setLeaveOpen(false); loadLeaves();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const setLeaveStatus = async (id, status) => {
    try { await api.put(`/leaves/${id}/status`, { status }); toast.success("Statut mis à jour"); loadLeaves(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="hr-page">
      <Toolbar>
        <div className="flex border border-border">
          {[{ k: "employees", l: "Employés" }, { k: "leaves", l: "Congés" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`hr-tab-${t.k}`}
              className={`px-5 py-2 text-sm font-medium transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
              {t.l}
            </button>
          ))}
        </div>
        {tab === "employees" ? (
          isAdmin && <Btn onClick={openNewEmp} data-testid="new-employee-btn"><Plus size={16} /> Nouvel employé</Btn>
        ) : (
          <Btn onClick={openNewLeave} data-testid="new-leave-btn"><Plus size={16} /> Demander un congé</Btn>
        )}
      </Toolbar>

      {tab === "employees" ? (
        employees.length === 0 ? (
          <EmptyState icon={UserCog} title="Aucun employé" sub={isAdmin ? "Ajoutez les membres de votre équipe pour gérer les postes, salaires et congés." : "Aucun employé enregistré pour l'instant."} action={isAdmin && <Btn onClick={openNewEmp}><Plus size={16} /> Ajouter</Btn>} />
        ) : (
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nom</th>
                  <th className="px-4 py-3 font-medium">Poste</th>
                  <th className="px-4 py-3 font-medium">Département</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  {isAdmin && <th className="px-4 py-3 text-right font-medium">Salaire</th>}
                  {isAdmin && <th className="px-4 py-3 text-right font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-border transition-colors last:border-0 hover:bg-accent/40" data-testid={`employee-row-${emp.id}`}>
                    <td className="px-4 py-3 font-medium">{emp.name}<div className="text-xs text-muted-foreground">{emp.email}</div></td>
                    <td className="px-4 py-3">{emp.position || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{emp.department || "—"}</td>
                    <td className="px-4 py-3"><Badge>{emp.status}</Badge></td>
                    {isAdmin && <td className="px-4 py-3 text-right font-mono">{euro(emp.salary)}</td>}
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEditEmp(emp)} data-testid={`edit-employee-${emp.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><Pencil size={16} /></button>
                          <button onClick={() => delEmp(emp.id)} data-testid={`delete-employee-${emp.id}`} className="p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : leaves.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Aucune demande de congé" sub="Les demandes de congés apparaîtront ici pour validation." action={<Btn onClick={openNewLeave}><Plus size={16} /> Demander</Btn>} />
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Employé</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Période</th>
                <th className="px-4 py-3 text-right font-medium">Jours</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                {isAdmin && <th className="px-4 py-3 text-right font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {leaves.map((lv) => (
                <tr key={lv.id} className="border-b border-border transition-colors last:border-0 hover:bg-accent/40" data-testid={`leave-row-${lv.id}`}>
                  <td className="px-4 py-3 font-medium">{lv.employee_name || "—"}</td>
                  <td className="px-4 py-3 capitalize">{lv.type}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{lv.start_date} → {lv.end_date}</td>
                  <td className="px-4 py-3 text-right font-mono">{lv.days}</td>
                  <td className="px-4 py-3"><Badge>{lv.status}</Badge></td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setLeaveStatus(lv.id, "approuvé")} data-testid={`approve-leave-${lv.id}`} title="Approuver" className="p-2 text-emerald-400 transition-colors hover:bg-emerald-500/10"><Check size={16} /></button>
                        <button onClick={() => setLeaveStatus(lv.id, "refusé")} data-testid={`reject-leave-${lv.id}`} title="Refuser" className="p-2 text-destructive transition-colors hover:bg-destructive/10"><X size={16} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Employee modal */}
      <Modal open={empOpen} onClose={() => setEmpOpen(false)} title={empEditId ? "Modifier l'employé" : "Nouvel employé"}>
        <form onSubmit={saveEmp} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nom"><input required className={inputCls} value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="employee-name" /></Field>
            <Field label="E-mail"><input type="email" className={inputCls} value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} data-testid="employee-email" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Poste"><input className={inputCls} value={empForm.position} onChange={(e) => setEmpForm({ ...empForm, position: e.target.value })} data-testid="employee-position" /></Field>
            <Field label="Département"><input className={inputCls} value={empForm.department} onChange={(e) => setEmpForm({ ...empForm, department: e.target.value })} data-testid="employee-department" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Téléphone"><input className={inputCls} value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} data-testid="employee-phone" /></Field>
            <Field label="Salaire (€)"><input type="number" step="0.01" className={inputCls} value={empForm.salary} onChange={(e) => setEmpForm({ ...empForm, salary: e.target.value })} data-testid="employee-salary" /></Field>
            <Field label="Embauche"><input type="date" className={inputCls} value={empForm.hire_date} onChange={(e) => setEmpForm({ ...empForm, hire_date: e.target.value })} data-testid="employee-hire" /></Field>
          </div>
          <Field label="Statut">
            <select className={inputCls} value={empForm.status} onChange={(e) => setEmpForm({ ...empForm, status: e.target.value })} data-testid="employee-status">
              <option value="actif">Actif</option>
              <option value="inactif">Inactif</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Btn type="button" variant="secondary" onClick={() => setEmpOpen(false)}>Annuler</Btn>
            <Btn type="submit" loading={saving} data-testid="save-employee-btn">Enregistrer</Btn>
          </div>
        </form>
      </Modal>

      {/* Leave modal */}
      <Modal open={leaveOpen} onClose={() => setLeaveOpen(false)} title="Demande de congé">
        <form onSubmit={saveLeave} className="space-y-4">
          <Field label="Employé">
            <select className={inputCls} value={leaveForm.employee_id} onChange={(e) => setLeaveForm({ ...leaveForm, employee_id: e.target.value })} data-testid="leave-employee">
              <option value="">— Sélectionner —</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type">
              <select className={inputCls} value={leaveForm.type} onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })} data-testid="leave-type">
                <option value="congé payé">Congé payé</option>
                <option value="maladie">Maladie</option>
                <option value="sans solde">Sans solde</option>
              </select>
            </Field>
            <Field label="Nombre de jours"><input type="number" step="0.5" className={inputCls} value={leaveForm.days} onChange={(e) => setLeaveForm({ ...leaveForm, days: e.target.value })} data-testid="leave-days" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Début"><input type="date" className={inputCls} value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} data-testid="leave-start" /></Field>
            <Field label="Fin"><input type="date" className={inputCls} value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} data-testid="leave-end" /></Field>
          </div>
          <Field label="Motif"><textarea rows={2} className={inputCls} value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} data-testid="leave-reason" /></Field>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Btn type="button" variant="secondary" onClick={() => setLeaveOpen(false)}>Annuler</Btn>
            <Btn type="submit" loading={saving} data-testid="save-leave-btn">Soumettre</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
